import { useState } from 'react';
import type { PipelineStageDto, CategoryRunsDto, TimerTriggerRunDto } from '../types';
import './StatusPage.css';

interface PipelineRunsTableProps {
  stages: PipelineStageDto[];
  categories: CategoryRunsDto[];
}

export function PipelineRunsTable({ stages, categories }: PipelineRunsTableProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const formatDuration = (ms: number | null): string => {
    if (ms === null) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusClass = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'success':
        return 'success';
      case 'failed':
      case 'error':
        return 'error';
      case 'in_progress':
      case 'running':
        return 'in-progress';
      default:
        return 'default';
    }
  };

  const renderRunRow = (run: TimerTriggerRunDto) => (
    <tr key={run.id} className="pipeline-runs__row">
      <td className="pipeline-runs__cell">{run.triggerType}</td>
      <td className="pipeline-runs__cell">{run.currentStage}</td>
      <td className="pipeline-runs__cell">
        <span className={`pipeline-runs__status pipeline-runs__status--${getStatusClass(run.status)}`}>
          {run.status}
        </span>
      </td>
      <td className="pipeline-runs__cell">{formatDateTime(run.actualRunTime)}</td>
      <td className="pipeline-runs__cell">{formatDuration(run.durationMs)}</td>
      <td className="pipeline-runs__cell">{run.itemsProcessed}</td>
      <td className="pipeline-runs__cell">{run.itemsClustered}</td>
      <td className="pipeline-runs__cell">{run.itemsSelected}</td>
      <td className="pipeline-runs__cell">
        {run.summaryGenerated ? (
          <span className="pipeline-runs__badge pipeline-runs__badge--success">Yes</span>
        ) : (
          <span className="pipeline-runs__badge pipeline-runs__badge--pending">No</span>
        )}
      </td>
      <td className="pipeline-runs__cell pipeline-runs__cell--error">
        {run.errorMessage || '-'}
      </td>
    </tr>
  );

  return (
    <div className="pipeline-runs">
      {/* Stage Overview */}
      <div className="pipeline-runs__stages">
        <h4 className="pipeline-runs__section-title">Stage Summary</h4>
        <div className="pipeline-runs__stages-grid">
          {stages.map(stage => (
            <div key={stage.stage} className="pipeline-runs__stage-card">
              <div className="pipeline-runs__stage-name">{stage.stage.replace('_', ' ')}</div>
              <div className="pipeline-runs__stage-stats">
                <div className="pipeline-runs__stage-stat">
                  <span className="pipeline-runs__stage-value">{stage.totalRuns}</span>
                  <span className="pipeline-runs__stage-label">Runs</span>
                </div>
                <div className="pipeline-runs__stage-stat">
                  <span className="pipeline-runs__stage-value pipeline-runs__stage-value--success">
                    {stage.successCount}
                  </span>
                  <span className="pipeline-runs__stage-label">Success</span>
                </div>
                <div className="pipeline-runs__stage-stat">
                  <span className="pipeline-runs__stage-value pipeline-runs__stage-value--error">
                    {stage.failedCount}
                  </span>
                  <span className="pipeline-runs__stage-label">Failed</span>
                </div>
                <div className="pipeline-runs__stage-stat">
                  <span className="pipeline-runs__stage-value">
                    {formatDuration(stage.totalDurationMs)}
                  </span>
                  <span className="pipeline-runs__stage-label">Duration</span>
                </div>
              </div>
              {stage.firstRun && stage.lastRun && (
                <div className="pipeline-runs__stage-times">
                  <span>First: {formatDateTime(stage.firstRun)}</span>
                  <span>Last: {formatDateTime(stage.lastRun)}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Category Runs */}
      <div className="pipeline-runs__categories">
        <h4 className="pipeline-runs__section-title">Category Runs</h4>
        {categories.map(category => {
          const categoryId = category.subCategorySlug
            ? `${category.categorySlug}-${category.subCategorySlug}`
            : category.categorySlug;
          const isExpanded = expandedCategories.has(categoryId);
          const categoryTitle = category.subCategoryName
            ? `${category.categoryName} > ${category.subCategoryName}`
            : category.categoryName;

          return (
            <div key={categoryId} className="pipeline-runs__category">
              <div
                className="pipeline-runs__category-header"
                onClick={() => toggleCategory(categoryId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCategory(categoryId);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <h5 className="pipeline-runs__category-title">{categoryTitle}</h5>
                <div className="pipeline-runs__category-info">
                  <span className="pipeline-runs__category-count">
                    {category.runs.length} runs
                  </span>
                  {category.summaryTracker?.allSubCategoriesComplete && (
                    <span className="pipeline-runs__badge pipeline-runs__badge--success">
                      Complete
                    </span>
                  )}
                  <button
                    className="pipeline-runs__category-toggle"
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? '−' : '+'}
                  </button>
                </div>
              </div>

              {isExpanded && category.runs.length > 0 && (
                <div className="pipeline-runs__table-container">
                  <table className="pipeline-runs__table">
                    <thead>
                      <tr>
                        <th>Trigger</th>
                        <th>Stage</th>
                        <th>Status</th>
                        <th>Run Time</th>
                        <th>Duration</th>
                        <th>Processed</th>
                        <th>Clustered</th>
                        <th>Selected</th>
                        <th>Summary</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {category.runs.map(run => renderRunRow(run))}
                    </tbody>
                  </table>
                </div>
              )}

              {isExpanded && category.runs.length === 0 && (
                <div className="pipeline-runs__empty">
                  No runs recorded for this category
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
